/**
 * F-047: React Query hook for the merchant order list.
 *
 * `useStoreOrders` — wraps `listStoreOrders` with `useInfiniteQuery`. Follows
 * the exact pattern established by `useOrders` (keepPreviousData, dedupeBy,
 * v5 getNextPageParam 4-arg signature).
 *
 * Admin-local because it binds to the admin-specific query key and
 * `storeId` dependency.
 */
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import type {
  AdminOrderReviewFilter,
  OrderStatus,
  StoreOrderRow,
  StoreOrderSortBy,
} from '@dukanti/shared';
import { listStoreOrders, ADMIN_ORDERS_PAGE_SIZE, dedupeBy } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface UseStoreOrdersOptions {
  readonly storeId: string | null;
  readonly status: OrderStatus | null;
  readonly reviewFilter?: AdminOrderReviewFilter | null;
  readonly sortBy: StoreOrderSortBy;
  readonly search: string | null;
  readonly limit?: number; // defaults to ADMIN_ORDERS_PAGE_SIZE
}

export interface UseStoreOrdersResult {
  rows: StoreOrderRow[];
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

/**
 * Infinite-query hook for the merchant order list.
 *
 * Page size defaults to ADMIN_ORDERS_PAGE_SIZE (20). Status filter is
 * included in the query key so switching filter tabs triggers a fresh
 * query rather than reusing cached pages. Incoming workflow filters use
 * `ordered` instead of the deprecated pending/confirmed lifecycle.
 *
 * Applies `dedupeBy(rows, r => r.id)` after `flatMap` — concurrent-insert
 * race mitigation.
 */
export function useStoreOrders(opts: UseStoreOrdersOptions): UseStoreOrdersResult {
  const {
    storeId,
    status,
    reviewFilter = null,
    sortBy,
    search,
    limit = ADMIN_ORDERS_PAGE_SIZE,
  } = opts;

  const q = useInfiniteQuery({
    queryKey: [
      'store-orders',
      storeId,
      status ?? 'all',
      reviewFilter ?? 'none',
      sortBy,
      search ?? '',
    ] as const,
    enabled: !!storeId,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listStoreOrders(supabase, {
        limit,
        offset: pageParam as number,
        status,
        reviewFilter,
        storeId: storeId as string,
        sortBy,
        search,
      }),
    // v5 signature: 4 args. allPageParams.length * limit gives the next offset.
    getNextPageParam: (last, _allPages, _lastPageParam, allPageParams) =>
      last.hasMore ? allPageParams.length * limit : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const rawRows = q.data?.pages.flatMap((p) => p.rows) ?? [];
  const rows = dedupeBy(rawRows, (r) => r.id);
  const hasMore = q.data?.pages[q.data.pages.length - 1]?.hasMore ?? false;

  return {
    rows,
    hasMore,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    hasNextPage: q.hasNextPage,
    isFetchingNextPage: q.isFetchingNextPage,
    fetchNextPage: () => void q.fetchNextPage(),
    refetch: () => void q.refetch(),
  };
}
